import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CalendarCheck,
  Clock,
  Loader2,
  MessageCircleQuestion,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  TriangleAlert,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ChatPanel } from "@/components/boi-de/tong-hop-classic";
import { Button } from "@/components/ui/button";

import { SilkReadingMarkdown } from "@/components/boi-de/silk-reading";
import { IconLaBan, IconVanKhi } from "@/components/nav-icons";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  openVanKhiChat,
  openVanKhiReading,
  type VanKhiKind,
  type VanKhiNotableDay,
  type VanKhiReading,
  type VanKhiReadingResult,
} from "@/lib/boide-api";

import { VanKhiLucky, VanKhiLuckyInline } from "./van-khi-lucky";
import { AlmanacGroup, AlmanacRow } from "./van-khi-row";
import { KIND_META, MUC_DO_META, scoreRingClass } from "./van-khi-style";
import { AiWatermark } from "@/components/ai-watermark";

/** Vòng điểm vận khí (thang 100) — cùng con số với thẻ vận khí ngày ở trang chủ. */
function ScoreRing({ score }: { score: number }) {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * circumference;

  return (
    <div className="relative size-[104px] shrink-0">
      <svg viewBox="0 0 100 100" className="size-full -rotate-90">
        <circle cx="50" cy="50" r={radius} className="fill-none stroke-muted/40" strokeWidth="7" />
        <circle
          cx="50"
          cy="50"
          r={radius}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          className={cn("fill-none stroke-current transition-all", scoreRingClass(score))}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[10px] tracking-wide text-muted-foreground">Điểm</span>
        <span className={cn("font-display text-2xl font-semibold", scoreRingClass(score))}>
          {score}
          <span className="text-xs text-muted-foreground">/100</span>
        </span>
      </div>
    </div>
  );
}

function NotableDayRow({ day, tone }: { day: VanKhiNotableDay; tone: "good" | "bad" }) {
  return (
    <li className="flex w-full min-w-0 items-start justify-between gap-2 rounded-xl border border-border/60 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="break-words text-sm leading-snug text-foreground/90 sm:truncate">
          {day.display}
        </p>
        <p className="mt-0.5 break-words text-[11px] leading-relaxed text-muted-foreground sm:truncate">
          {day.lunar_display} · {day.can_chi_ngay} · trực {day.truc} · sao {day.sao}
        </p>
      </div>
      <span
        className={cn(
          "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium",
          "border border-current/20",
          tone === "good" ? "text-element-wood" : "text-destructive",
        )}
      >
        {day.score}
      </span>
    </li>
  );
}

export function VanKhiReadingView({
  kind,
  periodKey,
  reading,
  withAlmanac = false,
}: {
  kind: VanKhiKind;
  periodKey: string;
  reading: VanKhiReading;
  /**
   * Trang đã hiện tờ lịch ngày ngay trên bài (điểm, xếp loại, can chi, giờ, hướng, màu
   * số). Khi đó bỏ những thứ ấy khỏi bài luận — hiện hai lần cùng một con số là thừa.
   */
  withAlmanac?: boolean;
}) {
  const skeleton = reading.skeleton;
  const detail = skeleton?.day_detail ?? null;
  const huong = skeleton?.huong ?? null;
  const nguHanh = skeleton?.ngu_hanh ?? null;
  // Kỳ tuần/tháng: lời khuyên, màu số, ngũ hành, hướng gom vào CHUNG thẻ tổng quan
  // (trước là bốn thẻ rời). Kỳ ngày đã có tờ lịch lo phần này.
  const compact = !withAlmanac && !detail;

  return (
    <div className="almanac-reading min-w-0 max-w-full flex flex-col gap-5">
      {reading.stale ? <StaleNotice kind={kind} periodKey={periodKey} /> : null}

      <section className="almanac-panel almanac-reading-overview">
        <div className="almanac-reading-heading">
          <div className="relative min-w-0 flex-1 space-y-3">
            <div className="flex items-center gap-2 text-primary">
              <IconVanKhi className="size-5" />
              <span className="text-xs font-semibold">Tổng quan vận khí</span>
            </div>
            <h2 className="almanac-reading-title">{reading.title}</h2>
            <p className="text-xs text-muted-foreground">
              {withAlmanac ? "" : reading.bucket}
              {skeleton?.tiet_khi ? `${withAlmanac ? "" : " · "}Tiết ${skeleton.tiet_khi}` : ""}
              {withAlmanac
                ? ""
                : kind === "day"
                  ? skeleton?.can_chi?.ngay
                    ? ` · ${skeleton.can_chi.ngay}`
                    : ""
                  : skeleton?.can_chi?.thang
                    ? ` · ${skeleton.can_chi.thang}`
                    : ""}
              {skeleton?.nap_am ? ` (${skeleton.nap_am.name})` : ""}
            </p>
            {reading.hashtags.length ? (
              <div className="flex flex-wrap gap-1.5">
                {/* Chữ bình thường, không phải hashtag — BE đã dọn '#'/gạch dưới. */}
                {reading.hashtags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-gold-soft/60 px-2.5 py-1 text-[11px] text-primary"
                  >
                    {tag.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          {withAlmanac ? null : <ScoreRing score={reading.energy_score} />}
        </div>
        {compact ? <PeriodFacts reading={reading} /> : null}
      </section>

      {/* Kỳ ngày: thẻ "Ngày này nên làm gì" của tờ lịch đã nói đúng việc này (tính sẵn,
          không tự mâu thuẫn) và đứng TRÊN bài luận — hiện thêm bản của thầy là trùng. */}
      {withAlmanac || compact ? null : <AdviceSection kind={kind} reading={reading} />}

      {withAlmanac || compact ? null : (
        <VanKhiLucky
          mayMan={skeleton?.may_man ?? detail?.may_man}
          tuoiHop={detail?.tuoi_hop}
          title={
            kind === "day"
              ? "Màu, số & tuổi hợp hôm nay"
              : kind === "week"
                ? "Màu & số hợp trong tuần"
                : "Màu & số hợp trong tháng"
          }
        />
      )}

      {/* ── Khí của kỳ tuần/tháng: ngũ hành + hướng (kỳ ngày nằm trong thẻ chi tiết) ── */}
      {!detail && !compact && (nguHanh || huong) ? (
        <Card className="almanac-panel">
          <CardContent className="grid gap-3 pt-6 sm:grid-cols-2">
            {nguHanh ? (
              <div className="rounded-xl border border-border/60 p-3">
                <p className="text-[11px] font-semibold text-muted-foreground">
                  Ngũ hành với bản mệnh
                </p>
                <p className="mt-1 text-sm text-foreground/90">{nguHanh.term}</p>
                <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                  {nguHanh.meaning}.
                </p>
              </div>
            ) : null}
            {huong ? (
              <div className="rounded-xl border border-border/60 p-3">
                <p className="flex items-center gap-1.5 text-[11px] tracking-wide text-muted-foreground">
                  <IconLaBan className="size-4 text-primary" /> Hướng xuất hành
                </p>
                <p className="mt-1 text-sm text-foreground/90">
                  Hỷ Thần {huong.hy_than} · Tài Thần {huong.tai_than}
                </p>
                {skeleton?.huong_ngay ? (
                  <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                    Theo ngày thuận nhất: {skeleton.huong_ngay}
                  </p>
                ) : null}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* ── Chi tiết ngày: trực / sao / giờ hoàng đạo / hướng ── */}
      {detail && !withAlmanac ? (
        <Card className="almanac-panel">
          <CardContent className="space-y-3 pt-6">
            <h3 className="font-display text-base font-bold text-foreground">
              Thời điểm và hướng tốt
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-border/60 p-3">
                <p className="text-[11px] font-semibold text-muted-foreground">Tính chất ngày</p>
                <p className="mt-1 text-sm text-foreground/90">
                  Trực {detail.truc.name} · sao {detail.sao.name}
                  <span
                    className={cn(
                      "ml-2 rounded-full border border-current/20 px-2 py-0.5 text-[10px]",
                      detail.is_hoang_dao ? "text-element-wood" : "text-destructive",
                    )}
                  >
                    {detail.is_hoang_dao ? "hoàng đạo" : "hắc đạo"}
                  </span>
                </p>
                <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                  {detail.truc.y_nghia}
                </p>
                {detail.ngay_ky?.length ? (
                  <p className="mt-1 text-[11px] leading-relaxed text-destructive">
                    Phạm {detail.ngay_ky.map((k) => k.name).join(", ")} — tránh việc trọng đại.
                  </p>
                ) : null}
              </div>
              <div className="rounded-xl border border-border/60 p-3">
                <p className="flex items-center gap-1.5 text-[11px] tracking-wide text-muted-foreground">
                  <IconLaBan className="size-4 text-primary" /> Hướng xuất hành
                </p>
                <p className="mt-1 text-sm text-foreground/90">
                  {huong
                    ? `Hỷ Thần ${huong.hy_than} · Tài Thần ${huong.tai_than}`
                    : skeleton?.direction}
                </p>
                {nguHanh ? (
                  <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                    {nguHanh.term}: {nguHanh.meaning}.
                  </p>
                ) : skeleton?.element_relation ? (
                  <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                    Khí ngày {skeleton.period_element} {skeleton.element_relation} bản mệnh{" "}
                    {skeleton.user_element}.
                  </p>
                ) : null}
              </div>
            </div>
            {detail.gio_hoang_dao.length ? (
              <div className="rounded-xl border border-border/60 p-3">
                <p className="flex items-center gap-1.5 text-[11px] tracking-wide text-muted-foreground">
                  <Clock className="size-3.5" /> Giờ hoàng đạo
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {detail.gio_hoang_dao.map((gio) => (
                    <span
                      key={gio.gio_chi}
                      className="rounded-lg border border-gold-soft/60 px-2 py-1 text-[11px] font-semibold text-primary"
                    >
                      {gio.gio_chi} {gio.khung_gio}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* ── Bài luận ── */}
      <Card className="almanac-panel">
        <CardContent className="pt-6">
          <h3 className="mb-3 font-display text-base font-bold text-foreground">
            Luận giải chi tiết
          </h3>
          <div className="almanac-reading-prose">
            <SilkReadingMarkdown body={reading.reading_md} />
          </div>
          <AiWatermark />
        </CardContent>
      </Card>

      {/* ── Nhịp trong kỳ (tuần: 7 ngày · tháng: 3 tuần) ── */}
      {reading.segments.length ? (
        <Card className="almanac-panel">
          <CardContent className="space-y-2 pt-6">
            <p className="flex items-center gap-1.5 text-sm font-medium text-primary">
              <CalendarCheck className="size-4" />
              {kind === "month" ? "Thượng · Trung · Hạ tuần" : "Nhịp từng ngày"}
            </p>
            <ul className="space-y-2">
              {reading.segments.map((segment) => {
                const meta = MUC_DO_META[segment.muc_do];
                return (
                  <li
                    key={segment.label}
                    className="rounded-xl border border-border/60 px-3 py-2.5"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-foreground/90">{segment.label}</span>
                      <span className={cn("text-[11px] font-medium", meta.text)}>{meta.label}</span>
                    </div>
                    <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted/40">
                      <div
                        className={cn("h-full rounded-full", meta.bar)}
                        style={{
                          width:
                            segment.muc_do === "cao"
                              ? "100%"
                              : segment.muc_do === "vua"
                                ? "62%"
                                : "30%",
                        }}
                      />
                    </div>
                    {segment.note ? (
                      <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                        {segment.note}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {/* ── Ngày đáng chú ý (kỳ tuần/tháng) ── */}
      {skeleton?.best_days?.length || skeleton?.caution_days?.length ? (
        <div className="grid min-w-0 max-w-full gap-4 sm:grid-cols-2">
          {skeleton.best_days?.length ? (
            <Card className="min-w-0 overflow-hidden almanac-panel">
              <CardContent className="min-w-0 space-y-2 px-4 pt-5 sm:px-6 sm:pt-6">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-element-wood">
                  <CalendarCheck className="size-4" /> Ngày thuận nhất
                </p>
                <ul className="min-w-0 space-y-2">
                  {skeleton.best_days.map((day) => (
                    <NotableDayRow key={day.solar_date} day={day} tone="good" />
                  ))}
                </ul>
              </CardContent>
            </Card>
          ) : null}
          {skeleton.caution_days?.length ? (
            <Card className="min-w-0 overflow-hidden almanac-panel">
              <CardContent className="min-w-0 space-y-2 px-4 pt-5 sm:px-6 sm:pt-6">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-destructive">
                  <TriangleAlert className="size-4" /> Ngày cần thận trọng
                </p>
                <ul className="min-w-0 space-y-2">
                  {skeleton.caution_days.map((day) => (
                    <NotableDayRow key={day.solar_date} day={day} tone="bad" />
                  ))}
                </ul>
              </CardContent>
            </Card>
          ) : null}
        </div>
      ) : null}

      {/* Khoá kèm tiêu đề: thầy luận lại thì phiên hỏi thêm cũ (mang bài cũ) phải đóng lại. */}
      <AskMore key={`${kind}:${periodKey}:${reading.title}`} kind={kind} periodKey={periodKey} />

      {!skeleton?.personalized ? (
        <p className="text-center text-xs leading-relaxed text-muted-foreground">
          Bài này luận theo khí chung. Bổ sung ngày sinh trong hồ sơ để vận khí tính riêng cho bạn.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Bài viết trên bảng điểm cũ. Điểm trên màn hình BE đã tính lại (khớp tờ lịch ngày), nhưng
 * lời văn thì không — thầy có thể đang khen một ngày nay đã tụt hạng. Mời luận lại miễn phí.
 */
function StaleNotice({ kind, periodKey }: { kind: VanKhiKind; periodKey: string }) {
  const queryClient = useQueryClient();
  const rewrite = useMutation({
    mutationFn: () => openVanKhiReading(kind, periodKey, undefined, { refresh: true }),
    onSuccess: (res) => {
      queryClient.setQueryData<VanKhiReadingResult>(["van-khi-reading", kind, periodKey], (prev) =>
        prev ? { ...prev, period: res.period, reading: res.reading } : prev,
      );
      if (res.rewritten) toast.success("Thầy đã luận lại theo điểm mới.");
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "Chưa luận lại được, thử lại sau nhé"),
  });

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-gold-soft/60 bg-primary/5 px-4 py-3 sm:flex-row sm:items-center">
      <RefreshCw className="hidden size-5 shrink-0 text-primary sm:block" />
      <p className="min-w-0 flex-1 text-xs leading-5 text-foreground/85">
        Điểm từng ngày đã được tính lại theo cách chấm mới, khớp với lịch ngày. Bài luận bên dưới
        viết theo bảng điểm cũ nên có thể lệch vài chỗ.
      </p>
      <Button
        size="sm"
        onClick={() => rewrite.mutate()}
        disabled={rewrite.isPending}
        className="shrink-0"
      >
        {rewrite.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
        {rewrite.isPending ? "Thầy đang luận…" : "Thầy luận lại (miễn phí)"}
      </Button>
    </div>
  );
}

/**
 * Hỏi thêm thầy: bài trên đã soạn sẵn đủ chi tiết; muốn đào sâu chuyện riêng thì mở
 * một phiên chat mang sẵn bài luận + dữ kiện của kỳ. Mở phiên miễn phí, mỗi lượt hỏi
 * tính phí theo token như mọi phiên chat Bói Đê.
 */
function AskMore({ kind, periodKey }: { kind: VanKhiKind; periodKey: string }) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const open = useMutation({
    mutationFn: () => openVanKhiChat(kind, periodKey),
    onSuccess: (res) => setSessionId(res.session_id),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Chưa mở được phiên hỏi thêm"),
  });

  if (sessionId) {
    return (
      <ChatPanel
        sessionId={sessionId}
        title="Hỏi thêm thầy"
        emptyHint="Hỏi sâu về một việc cụ thể trong kỳ này — thầy trả lời theo đúng bài luận và lịch ở trên."
        hideInitialAssistant
        variant="silk"
      />
    );
  }

  return (
    <Card className="almanac-panel">
      <CardContent className="flex flex-col items-center gap-3 pt-6 text-center sm:flex-row sm:text-left">
        <span className="grid size-11 shrink-0 place-items-center rounded-full border border-gold-soft/60 text-primary">
          <MessageCircleQuestion className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-base font-bold text-foreground">Muốn hỏi sâu hơn?</p>
          <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
            Hỏi thầy về một việc cụ thể (ký hợp đồng, đi xa, tỏ tình…) trong kỳ này. Mỗi lượt hỏi
            tính phí theo token (1đ/token).
          </p>
        </div>
        <Button onClick={() => open.mutate()} disabled={open.isPending} className="shrink-0">
          {open.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
          Hỏi thêm thầy
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Kỳ tuần/tháng: mọi dữ kiện ngắn của kỳ trong một khuôn "nhãn trái – nội dung phải",
 * cùng kiểu với tờ lịch ngày. Màu chỉ hiện chấm (tên ở tooltip).
 */
function PeriodFacts({ reading }: { reading: VanKhiReading }) {
  const skeleton = reading.skeleton;
  const huong = skeleton?.huong ?? null;
  const nguHanh = skeleton?.ngu_hanh ?? null;
  const list = (items: string[], dot: string) => (
    <ul className="space-y-1">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <span className={cn("mt-2 size-1.5 shrink-0 rounded-full", dot)} />
          <span className="min-w-0 flex-1 text-foreground/85">{item}</span>
        </li>
      ))}
    </ul>
  );
  return (
    <>
      {reading.nen.length || reading.tranh.length ? (
        <AlmanacGroup>
          {reading.nen.length ? (
            <AlmanacRow icon={<Sparkles className="text-element-wood" />} label="Nên ưu tiên">
              {list(reading.nen, "bg-element-wood")}
            </AlmanacRow>
          ) : null}
          {reading.tranh.length ? (
            <AlmanacRow icon={<ShieldAlert className="text-destructive" />} label="Nên hạn chế">
              {list(reading.tranh, "bg-destructive")}
            </AlmanacRow>
          ) : null}
        </AlmanacGroup>
      ) : null}
      <VanKhiLuckyInline mayMan={skeleton?.may_man} />
      {nguHanh || huong ? (
        <AlmanacGroup>
          {nguHanh ? (
            <AlmanacRow icon={<IconVanKhi />} label="Ngũ hành">
              <strong className="font-semibold">{nguHanh.term}</strong>
              <span className="text-[12px] text-muted-foreground"> — {nguHanh.meaning}.</span>
            </AlmanacRow>
          ) : null}
          {huong ? (
            <AlmanacRow icon={<IconLaBan />} label="Hướng đi">
              <span className="text-foreground/90">
                Hỷ Thần <strong className="font-semibold">{huong.hy_than}</strong> · Tài Thần{" "}
                <strong className="font-semibold">{huong.tai_than}</strong>
              </span>
              {skeleton?.huong_ngay ? (
                <span className="block text-[11px] text-muted-foreground">
                  Theo ngày thuận nhất: {skeleton.huong_ngay}
                </span>
              ) : null}
            </AlmanacRow>
          ) : null}
        </AlmanacGroup>
      ) : null}
    </>
  );
}

function AdviceSection({ kind, reading }: { kind: VanKhiKind; reading: VanKhiReading }) {
  if (!reading.nen.length && !reading.tranh.length) return null;

  return (
    <section aria-labelledby="fortune-advice-title" className="almanac-advice">
      <h3 id="fortune-advice-title" className="mb-3 font-display text-lg font-bold text-foreground">
        {KIND_META[kind].advice}
      </h3>
      <div className="almanac-advice-grid">
        {reading.nen.length ? (
          <article className="almanac-advice-column">
            <h4 className="flex items-center gap-2 text-sm font-bold text-element-wood">
              <Sparkles className="size-4" /> Nên ưu tiên
            </h4>
            <ul className="mt-3 space-y-2">
              {reading.nen.map((item) => (
                <li key={item} className="flex gap-2 text-sm leading-6 text-foreground/85">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-element-wood" />
                  <span className="min-w-0 flex-1">{item}</span>
                </li>
              ))}
            </ul>
          </article>
        ) : null}
        {reading.tranh.length ? (
          <article className="almanac-advice-column">
            <h4 className="flex items-center gap-2 text-sm font-bold text-destructive">
              <ShieldAlert className="size-4" /> Nên hạn chế
            </h4>
            <ul className="mt-3 space-y-2">
              {reading.tranh.map((item) => (
                <li key={item} className="flex gap-2 text-sm leading-6 text-foreground/85">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-destructive" />
                  <span className="min-w-0 flex-1">{item}</span>
                </li>
              ))}
            </ul>
          </article>
        ) : null}
      </div>
    </section>
  );
}
